import { useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { submitLead } from "../../lib/api";
import { PROPERTY_TYPES, SERVICE_OPTIONS } from "../../data/content";

export default function ContactForm({
  defaultService = "",
  leadType = "site_inspection",
  source = "contact",
  heading = "Request a Free Site Inspection",
  subheading = "Tell us about your project. Our team can inspect the site and recommend a suitable solution.",
}) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    property_location: "",
    property_type: "",
    service: defaultService,
    area: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and mobile number are required.");
      return;
    }
    setLoading(true);
    try {
      const clean = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined),
      );
      await submitLead({
        ...clean,
        name: form.name,
        phone: form.phone,
        lead_type: leadType,
        metadata: { source },
      });
      toast.success(
        "Thank you. Urban Painter will contact you regarding your project.",
      );
      setSubmitted(true);
      setForm({
        name: "",
        phone: "",
        property_location: "",
        property_type: "",
        service: defaultService,
        area: "",
      });
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-form-wrapper" className="w-full">
      {heading && (
        <div className="mb-8">
          <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink-muted mb-4">
            Enquiry Form
          </div>
          <h2 className="font-display text-2xl md:text-4xl text-ink leading-tight tracking-[-0.02em]">
            {heading}
          </h2>
          {subheading && (
            <p className="mt-3 text-ink-muted leading-relaxed max-w-xl">
              {subheading}
            </p>
          )}
        </div>
      )}

      {submitted && (
        <div
          data-testid="form-success-banner"
          className="mb-6 border border-ink bg-ink text-white p-5"
        >
          <div className="font-display text-lg">Thank you.</div>
          <p className="text-white/75 text-sm mt-1">
            Urban Painter will contact you regarding your project.
          </p>
        </div>
      )}

      <form
        data-testid="contact-form"
        onSubmit={submit}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5"
      >
        <Field label="Name *">
          <Input
            data-testid="form-input-name"
            value={form.name}
            onChange={set("name")}
            required
            placeholder="Full name"
            className="h-12 rounded-none border-ink-line focus-visible:ring-ink focus-visible:ring-1"
          />
        </Field>
        <Field label="Mobile Number *">
          <Input
            data-testid="form-input-phone"
            value={form.phone}
            onChange={set("phone")}
            required
            placeholder="+91 ..."
            className="h-12 rounded-none border-ink-line focus-visible:ring-ink focus-visible:ring-1"
          />
        </Field>

        <Field label="Property Location">
          <Input
            data-testid="form-input-location"
            value={form.property_location}
            onChange={set("property_location")}
            placeholder="Sector 45, Gurugram"
            className="h-12 rounded-none border-ink-line focus-visible:ring-ink focus-visible:ring-1"
          />
        </Field>
        <Field label="Property Type">
          <Select value={form.property_type} onValueChange={set("property_type")}>
            <SelectTrigger
              data-testid="form-select-property-type"
              className="h-12 rounded-none border-ink-line focus:ring-ink focus:ring-1"
            >
              <SelectValue placeholder="Select property type" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Select Service">
          <Select value={form.service} onValueChange={set("service")}>
            <SelectTrigger
              data-testid="form-select-service"
              className="h-12 rounded-none border-ink-line focus:ring-ink focus:ring-1"
            >
              <SelectValue placeholder="Choose a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Approximate Area">
          <Input
            data-testid="form-input-area"
            value={form.area}
            onChange={set("area")}
            placeholder="e.g. 1200 sqft"
            className="h-12 rounded-none border-ink-line focus-visible:ring-ink focus-visible:ring-1"
          />
        </Field>

        <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 sm:items-center justify-between pt-2">
          <p className="text-xs text-ink-muted max-w-md">
            By submitting you agree to be contacted by Urban Painter regarding
            your project.
          </p>
          <button
            type="submit"
            data-testid="form-submit-button"
            disabled={loading}
            className="group inline-flex items-center justify-center gap-3 bg-ink hover:bg-ink-soft disabled:opacity-60 text-white px-8 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                Request Inspection
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover:translate-x-1"
                />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Label className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-muted">
        {label}
      </Label>
      {children}
    </div>
  );
}
